import { useCallback, useEffect, useState } from 'react'
import { lumi } from '@/lib/lumi'
import type { Job, JobCategory, ContractType } from '@/data/jobs'
import { emailRecruiter_JobPending } from '@/lib/emails'

/**
 * Recruiter job stored in MongoDB.
 * Maps to the static `Job` shape used throughout the UI, plus Mongo metadata.
 */
export interface RecruiterJob {
  _id: string
  title: string
  employer: string
  location: string
  department: string
  /** Région française (ex. "Île-de-France") — utilisée pour matcher les alertes emploi. */
  region?: string
  category: JobCategory
  contractType: ContractType
  salary?: string
  hourlyRate?: string
  description: string
  requirements: string[]
  benefits: string[]
  publishedAt: string
  deadline?: string
  /** Date de début souhaitée (YYYY-MM-DD) — tous contrats sauf AE & Intérim. */
  startDate?: string
  /** Date de fin (YYYY-MM-DD) — uniquement CDD & Temps Partiel CDD. */
  endDate?: string
  creator: string
  createdAt: string
  updatedAt: string
  validationStatus?: string
  rejectionReason?: string
  // Métadonnées recruteur (utilisées pour les emails admin → recruteur)
  creatorEmail?: string
  creatorName?: string
  // Champs optionnels enrichis
  urgent?: boolean
  logementPossible?: boolean
  horairesFixes?: boolean
  coursParticuliers?: boolean
  possibiliteCDI?: boolean
  // Logo recruteur (jointure côté client depuis company_profiles)
  logoUrl?: string
  // Mission ponctuelle (AE & intérim) — un seul jour
  missionDate?: string        // YYYY-MM-DD
  missionStartTime?: string   // HH:MM
  missionEndTime?: string     // HH:MM
  missionHours?: number       // durée en heures
  missionPrice?: number       // prix total mission (intérim, en €)
  // Activités cochées (AE & intérim) — Aquagym, Enseignement scolaire, etc.
  activities?: string[]
  /** Identifiant du lot d'import en masse (`yyyymmdd-hhmmss-rand`) — facultatif. */
  bulkBatchId?: string
}

export type NewRecruiterJob = Omit<RecruiterJob, '_id' | 'creator' | 'createdAt' | 'updatedAt' | 'publishedAt' | 'validationStatus'> & {
  publishedAt?: string
}

/**
 * Convert a database recruiter job into the UI `Job` shape.
 */
export function recruiterJobToJob(rj: RecruiterJob): Job {
  return {
    id: rj._id,
    title: rj.title,
    employer: rj.employer,
    location: rj.location,
    department: rj.department,
    region: rj.region,
    category: rj.category,
    contractType: rj.contractType,
    salary: rj.salary,
    hourlyRate: rj.hourlyRate,
    description: rj.description,
    requirements: rj.requirements || [],
    benefits: rj.benefits || [],
    publishedAt: (rj.publishedAt || rj.createdAt || '').slice(0, 10),
    deadline: rj.deadline,
    startDate: rj.startDate,
    endDate: rj.endDate,
    urgent: rj.urgent,
    logementPossible: rj.logementPossible,
    horairesFixes: rj.horairesFixes,
    coursParticuliers: rj.coursParticuliers,
    possibiliteCDI: rj.possibiliteCDI,
    logo: rj.logoUrl,
    missionDate: rj.missionDate,
    missionStartTime: rj.missionStartTime,
    missionEndTime: rj.missionEndTime,
    missionHours: rj.missionHours,
    missionPrice: rj.missionPrice,
    activities: rj.activities,
  }
}

export function useRecruiterJobs() {
  const [jobs, setJobs] = useState<RecruiterJob[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchJobs = useCallback(async (filter?: Record<string, unknown>) => {
    setLoading(true)
    setError(null)
    try {
      const { list } = await lumi.entities.recruiter_jobs.list({
        filter: filter ?? {},
        sort: { createdAt: -1 },
      })
      setJobs(list as RecruiterJob[])
      return list as RecruiterJob[]
    } catch (e) {
      console.error('recruiter_jobs.list failed:', e)
      setError('Impossible de charger les offres recruteurs.')
      return []
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchMyJobs = useCallback(async (recruiterId: string) => {
    return fetchJobs({ creator: recruiterId })
  }, [fetchJobs])

  const createJob = useCallback(
    async (data: NewRecruiterJob, recruiterId: string, recruiterEmail?: string, recruiterName?: string) => {
      const now = new Date().toISOString()
      const payload = {
        title: data.title,
        employer: data.employer,
        location: data.location,
        department: data.department,
        region: data.region || '',
        category: data.category,
        contractType: data.contractType,
        salary: data.salary || '',
        // hourlyRate uniquement pour auto-entrepreneur
        hourlyRate: data.contractType === 'auto-entrepreneur' ? (data.hourlyRate || '') : '',
        description: data.description,
        requirements: data.requirements || [],
        benefits: data.benefits || [],
        publishedAt: data.publishedAt || now,
        deadline: data.deadline || '',
        // Date de début souhaitée — vidée pour AE/Intérim (mission ponctuelle à la place)
        startDate: (data.contractType === 'auto-entrepreneur' || data.contractType === 'interim') ? '' : (data.startDate || ''),
        // Date de fin — uniquement pour CDD & Temps Partiel CDD
        endDate: (data.contractType === 'CDD' || data.contractType === 'temps-partiel-CDD') ? (data.endDate || '') : '',
        creator: recruiterId,
        // Métadonnées recruteur stockées pour permettre les emails admin → recruteur
        creatorEmail: recruiterEmail || '',
        creatorName: recruiterName || '',
        // Validation admin : toute nouvelle offre est en attente par défaut
        validationStatus: 'pending' as const,
        createdAt: now,
        updatedAt: now,
        // Champs optionnels enrichis
        urgent: data.urgent ?? false,
        logementPossible: data.logementPossible ?? false,
        horairesFixes: data.horairesFixes ?? false,
        coursParticuliers: data.category === 'MNS' ? (data.coursParticuliers ?? false) : false,
        possibiliteCDI: data.contractType === 'CDD' ? (data.possibiliteCDI ?? false) : false,
        // Mission ponctuelle — uniquement pour AE et Intérim
        missionDate: (data.contractType === 'auto-entrepreneur' || data.contractType === 'interim') ? (data.missionDate || '') : '',
        missionStartTime: (data.contractType === 'auto-entrepreneur' || data.contractType === 'interim') ? (data.missionStartTime || '') : '',
        missionEndTime: (data.contractType === 'auto-entrepreneur' || data.contractType === 'interim') ? (data.missionEndTime || '') : '',
        missionHours: (data.contractType === 'auto-entrepreneur' || data.contractType === 'interim') && typeof data.missionHours === 'number' ? data.missionHours : undefined,
        missionPrice: data.contractType === 'interim' && typeof data.missionPrice === 'number' ? data.missionPrice : undefined,
        // Activités cochées — uniquement pour AE et Intérim
        activities: (data.contractType === 'auto-entrepreneur' || data.contractType === 'interim') && Array.isArray(data.activities) ? data.activities : [],
        // Lot d'import en masse — facultatif (utilisé par BulkJobImport)
        bulkBatchId: data.bulkBatchId || '',
      }
      const created = await lumi.entities.recruiter_jobs.create(payload)
      console.log('recruiter_jobs.create OK', created)

      // Email recruteur — annonce en attente de validation (best-effort)
      if (recruiterEmail) {
        void emailRecruiter_JobPending({
          recruiterEmail,
          recruiterName,
          jobTitle: data.title,
          jobEmployer: data.employer,
          category: data.category,
          contractType: data.contractType,
        })
      }

      return created as RecruiterJob
    },
    []
  )

  const updateJob = useCallback(
    async (id: string, updates: Partial<NewRecruiterJob>) => {
      if (typeof id !== 'string' || !id) {
        throw new Error('Invalid job id')
      }
      const payload = {
        ...updates,
        updatedAt: new Date().toISOString(),
      }
      const updated = await lumi.entities.recruiter_jobs.update(id, payload)
      console.log('recruiter_jobs.update OK', id)
      return updated
    },
    []
  )

  const deleteJob = useCallback(async (id: string) => {
    if (typeof id !== 'string' || !id) {
      throw new Error('Invalid job id')
    }
    await lumi.entities.recruiter_jobs.delete(id)
    console.log('recruiter_jobs.delete OK', id)
  }, [])

  return {
    jobs,
    loading,
    error,
    fetchJobs,
    fetchMyJobs,
    createJob,
    updateJob,
    deleteJob,
    setJobs,
  }
}

/** Délai minimal entre deux requêtes Lumi pour éviter le rate-limit 10429 */
const INTER_REQUEST_DELAY_MS = 400

/** Pause utilitaire */
function sleep(ms: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, ms))
}

/**
 * Récupère les logos des recruteurs avec retry exponentiel
 * pour absorber les erreurs de rate-limit Lumi (code 10429).
 */
async function fetchLogosWithRetry(
  creators: string[],
  maxAttempts = 3
): Promise<Record<string, { logoUrl?: string }>> {
  const { fetchCompanyProfilesByCreators } = await import('@/hooks/useCompanyProfile')
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fetchCompanyProfilesByCreators(creators)
    } catch (e: unknown) {
      const code = (e as { code?: number })?.code
      if (code === 10429 && attempt < maxAttempts) {
        // Backoff exponentiel : 600ms, 1200ms…
        await sleep(600 * attempt)
        continue
      }
      console.warn('[useAllJobs] fetchLogos attempt', attempt, 'failed', e)
      return {}
    }
  }
  return {}
}

export function useAllJobs() {
  const [jobs, setJobs] = useState<RecruiterJob[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      setLoading(true)
      try {
        // Étape 1 : charger les offres
        const { list } = await lumi.entities.recruiter_jobs.list({
          sort: { createdAt: -1 },
        })
        const rawJobs = list as RecruiterJob[]
        if (cancelled) return

        // Étape 2 : pause anti-rate-limit avant la 2e requête
        await sleep(INTER_REQUEST_DELAY_MS)
        if (cancelled) return

        // Étape 3 : enrichir avec les logos (avec retry)
        const creators = Array.from(
          new Set(rawJobs.map((j) => j.creator).filter(Boolean))
        )
        const logoMap = creators.length > 0
          ? await fetchLogosWithRetry(creators)
          : {}
        if (cancelled) return

        // Étape 4 : fusionner logos dans les offres
        const enriched = rawJobs.map((j) => ({
          ...j,
          logoUrl: j.logoUrl || logoMap[j.creator]?.logoUrl,
        }))
        setJobs(enriched)
      } catch (e) {
        console.error('useAllJobs failed:', e)
        if (!cancelled) setJobs([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [])

  return { jobs, loading }
}
